import { gql, useQuery } from '@apollo/client';
import { Box, Skeleton } from '@mui/material';
import VideoPlayer from 'components/VideoPlayer';
import React from 'react';
import LiveBadge from './LiveBadge';
import ZoomAction from './ZoomAction';
import { Account } from 'types/Account';
import CameraPlayerBar from './CameraPlayerBar';

type CameraLive = {
  url: string;
  token: string;
};

const CAMERA_LIVE = gql`
  query GetCameraLive(
    $isCloud: Boolean!
    $masterCompanyId: Int!
    $accountId: Int!
    $accountType: Int!
    $doorId: Int
    $email: String
    $isSIMCloud: Boolean
    $isRTSP: Boolean
    $userName: String
    $camName: String!
  ) {
    getCameraLive(
      isCloud: $isCloud
      masterCompanyId: $masterCompanyId
      accountId: $accountId
      accountType: $accountType
      doorId: $doorId
      email: $email
      isSIMCloud: $isSIMCloud
      isRTSP: $isRTSP
      userName: $userName
      camName: $camName
    ) {
      url
      token
    }
  }
`;

function LiveCamera({
  paused = false,
  muted = false,
  widescreen = false,
  enableZoom = true,
  camName,
  accountSelected,
}: {
  paused?: boolean;
  muted?: boolean;
  widescreen?: boolean;
  enableZoom?: boolean;
  camName: string;
  accountSelected: Account;
}) {
  const [zoom, setZoom] = React.useState(1);
  const onZoomChange = React.useCallback((rZoom: number) => {
    setZoom(rZoom);
  }, []);
  const { data, refetch, error } = useQuery<{ getCameraLive: CameraLive }>(CAMERA_LIVE, {
    fetchPolicy: 'no-cache',
    pollInterval: 60000,
    variables: {
      ...accountSelected,
      accountId: accountSelected.id,
      camName,
    },
  });

  React.useEffect(
    function retryOnError() {
      let timeoutId: NodeJS.Timeout | undefined;
      if (error) {
        timeoutId = setTimeout(() => refetch(), 1000);
      }
      return () => clearTimeout(timeoutId);
    },
    [error, refetch],
  );

  const sources = React.useMemo(() => {
    if (data) {
      return [
        {
          src: data.getCameraLive.url,
        },
      ];
    }
  }, [data]);

  const onErrorPlaylist = React.useCallback(() => {
    refetch();
  }, [refetch]);

  if (!data) {
    return <Skeleton sx={{ flex: '1 1 auto', transform: 'none' }} />;
  }

  return (
    <Box
      overflow="hidden"
      flex="1 1 auto"
      role="region"
      aria-label="live video"
      position="relative"
      display="flex"
      maxHeight="662px"
      borderRadius="5px"
    >
      {accountSelected.isSIMCloud ? (
        <VideoPlayer.HLS
          {...{
            paused,
            muted,
            widescreen,
            sources,
            onZoomChange,
            zoom,
            enableZoom,
            onErrorPlaylist,
            authorization: data.getCameraLive.token,
          }}
          key={`channel-${camName}`}
          id={`channel-${camName}`}
          live
          autoplay
          fill
          noControlBar
          preload="auto"
        />
      ) : (
        <VideoPlayer.MJPEG id={`channel-${camName}`} url={data.getCameraLive.url} />
      )}

      <CameraPlayerBar {...{ camName, accountSelected }} />
      <LiveBadge />
      <ZoomAction {...{ zoom, setZoom }} />
    </Box>
  );
}

export default LiveCamera;
